import { getAuthScheme } from "@/config/runtime";
import connector from "@/repository/connector";

export default {
  async list(params = {}) {
    return connector.requestWithFallback(
      () => connector.connector.get("donations/", { params }),
    );
  },
  /**
   * GET donations/get_widget_donations/ — widget_token в Authorization как access token:
   * `${getAuthScheme()} ${token}` (по умолчанию Bearer). В query — limit, offset, status, ordering и т.д.
   */
  async getWidgetDonationList(params = {}) {
    const { widget_token: widgetToken, ...queryParams } = params;
    const token = String(widgetToken ?? "").trim();
    const authScheme = getAuthScheme();
    return connector.requestWithFallback(
      () =>
        connector.unauthenticatedConnector.get("donations/get_widget_donations/", {
          params: queryParams,
          headers: {
            Authorization: `${authScheme} ${token}`,
          },
        }),
      undefined,
      { skipAuthRefresh: true, skipUnauthorizedHandler: true },
    );
  },
  /**
   * GET widget-video-settings/{id}/ — для встраиваемого виджета (Authorization: widget_token).
   */
  async getWidgetVideoSettingsWithToken(id, params = {}) {
    const { widget_token: widgetToken } = params;
    const token = String(widgetToken ?? "").trim();
    const authScheme = getAuthScheme();
    return connector.requestWithFallback(
      () =>
        connector.unauthenticatedConnector.get(`widget-video-settings/${id}/`, {
          headers: {
            Authorization: `${authScheme} ${token}`,
          },
        }),
      undefined,
      { skipAuthRefresh: true, skipUnauthorizedHandler: true },
    );
  },
  async get(id) {
    return connector.requestWithFallback(
      () => connector.connector.get(`donations/${id}/`),
    );
  },
  async remove(id) {
    return connector.requestWithFallback(
      () => connector.connector.delete(`donations/${id}/`),
    );
  },
  async listUnionByDonorNames(params = {}) {
    return connector.requestWithFallback(
      () =>
        connector.connector.get("donations/get_union_by_donor_names/", {
          params,
        }),
    );
  },
  /**
   * GET donations/history/ — история донатов текущего авторизованного донатера (JWT).
   * Query: limit, offset, ordering (whitelist, например "-processed_at").
   */
  async listHistory(params = {}) {
    return connector.requestWithFallback(
      () =>
        connector.connector.get("donations/history/", {
          params,
        }),
    );
  },
  async set(payload) {
    return connector.requestWithFallback(
      () => connector.connector.post(`donations/`, payload),
    );
  },
  async getCost(payload) {
    return connector.requestWithFallback(
      () => connector.connector.post("donations/get_cost/", payload),
    );
  },
  async sendAlert(id, payload = {}) {
    return connector.requestWithFallback(
      () => connector.connector.post(`donations/${id}/send_alert/`, payload),
    );
  },
  async skipAlert(id) {
    return connector.requestWithFallback(
      () => connector.connector.post(`donations/${id}/skip_alert/`, {}),
    );
  },
  async allowModeration(id, payload) {
    return connector.requestWithFallback(
      () =>
        connector.connector.post(`donations/${id}/allow_moderation/`, payload),
    );
  },
  /**
   * POST donations/{id}/widget_send_alert/ — повтор оповещения из виджета.
   * widget_token в Authorization (как у getWidgetDonationList).
   */
  async widgetSendAlert(id, { widget_token: widgetToken, payload = {} } = {}) {
    const token = String(widgetToken ?? "").trim();
    const authScheme = getAuthScheme();
    return connector.requestWithFallback(
      () =>
        connector.unauthenticatedConnector.post(
          `donations/${id}/widget_send_alert/`,
          payload,
          {
            headers: {
              Authorization: `${authScheme} ${token}`,
            },
          },
        ),
      undefined,
      { skipAuthRefresh: true, skipUnauthorizedHandler: true },
    );
  },
  /**
   * POST donations/{id}/widget_skip/ — скип из виджета.
   * widget_token в Authorization.
   */
  async widgetSkip(id, { widget_token: widgetToken, payload = {} } = {}) {
    const token = String(widgetToken ?? "").trim();
    const authScheme = getAuthScheme();
    return connector.requestWithFallback(
      () =>
        connector.unauthenticatedConnector.post(
          `donations/${id}/widget_skip/`,
          payload,
          {
            headers: {
              Authorization: `${authScheme} ${token}`,
            },
          },
        ),
      undefined,
      { skipAuthRefresh: true, skipUnauthorizedHandler: true },
    );
  },
  /**
   * POST donations/{id}/widget_allow_moderation/ — одобрить модерацию из виджета (Authorization: widget_token).
   */
  async widgetAllowModeration(
    id,
    { widget_token: widgetToken, payload = {} } = {},
  ) {
    const token = String(widgetToken ?? "").trim();
    const authScheme = getAuthScheme();
    return connector.requestWithFallback(
      () =>
        connector.unauthenticatedConnector.post(
          `donations/${id}/widget_allow_moderation/`,
          payload,
          {
            headers: {
              Authorization: `${authScheme} ${token}`,
            },
          },
        ),
      undefined,
      { skipAuthRefresh: true, skipUnauthorizedHandler: true },
    );
  },
  async dynamics(payload) {
    return connector.requestWithFallback(
      () =>
        connector.connector.post("donations/dynamics/", {
          start: payload.start,
          end: payload.end,
          count: payload.count,
        }),
    );
  },
  async exportCsv(payload) {
    return connector.requestWithFallback(
      () =>
        connector.connector.post(
          "donations/export_csv/",
          {
            start: payload.start,
            end: payload.end,
            union_by_donor_names: Boolean(payload.union_by_donor_names),
          },
          { responseType: "blob" }
        ),
    );
  },
};
